import React from 'react';
import { formatWeight } from '../../utils/formatHelpers';

const CHART_LIMIT = 10;

const ItemsChart = ({ data, loading, drillData, drillLoading, onItemClick, onDrillClose }) => {
  if (loading) return <div className="chart-loading">Loading item data...</div>;
  if (!data || data.length === 0) {
    return <div className="chart-empty">No item data available for this period.</div>;
  }

  const top = [...data]
    .sort((a, b) => (parseFloat(b.total_cases) || 0) - (parseFloat(a.total_cases) || 0))
    .slice(0, CHART_LIMIT);

  const maxVal = Math.max(...top.map(d => d.total_cases || 0), 1);

  return (
    <div className="chart-section">
      <div className="chart-caption">Top {CHART_LIMIT} by cases</div>

      <div className="items-rank-list">
        {top.map((row, idx) => {
          const pct     = Math.round((row.total_cases / maxVal) * 100);
          const isActive = drillData?.item_id === row.item_id;

          return (
            <div
              key={row.item_id}
              className={`item-rank-row ${isActive ? 'item-rank-row--active' : ''}`}
              onClick={() => onItemClick && onItemClick(row)}
              role="button"
              tabIndex={0}
              aria-label={`${row.item_name}: ${row.total_cases} cases shipped. Click for details.`}
              onKeyDown={e => e.key === 'Enter' && onItemClick && onItemClick(row)}
            >
              <div className={`item-rank-badge ${idx === 0 ? 'rank-gold' : idx === 1 ? 'rank-silver' : idx === 2 ? 'rank-bronze' : ''}`}>
                {idx + 1}
              </div>
              <div className="item-rank-info">
                <div className="item-rank-name">{row.item_name}</div>
                <div className="item-rank-part">{row.part_number}</div>
              </div>
              <div className="item-rank-bar-wrap">
                <div className="item-rank-bar">
                  <div className="item-rank-bar__fill" style={{ width: pct + '%' }} />
                </div>
              </div>
              <div className="item-rank-stats">
                <span className="item-stat-main">{row.total_cases} cases</span>
                <span className="item-stat-sub">{formatWeight(row.total_weight)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {drillData && (
        <div className="drill-down">
          <div className="drill-down__header">
            <h3 className="drill-down__title">
              {drillData.item_name} — Pallets Containing This Item
            </h3>
            <button className="drill-down__close" onClick={onDrillClose} aria-label="Close drill-down">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Close
            </button>
          </div>

          {drillLoading ? (
            <div className="drill-down__loading">Loading...</div>
          ) : !drillData.rows || drillData.rows.length === 0 ? (
            <div className="drill-down__empty">No pallets found for this item.</div>
          ) : (
            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Pallet #</th>
                    <th>Customer</th>
                    <th>Qty (cases)</th>
                    <th>Weight</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.rows.map((p, idx) => (
                    <tr key={p.id || idx}>
                      <td className="pallet-number">{p.pallet_number}</td>
                      <td>{p.customer_name || '-'}</td>
                      <td>{p.quantity}</td>
                      <td>{formatWeight(p.subtotal_weight)}</td>
                      <td><span className={`status-badge ${p.pallet_status || p.status}`}>{p.pallet_status || p.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ItemsChart;
